import { useEffect, useState } from "react";
//old import { Info } from "lucide-react";
import { Info, RefreshCw, AlertTriangle } from "lucide-react";
import api from "../../api/api";
//old import { Panel, Stat, formatMonth, useOrgStyles } from "../../components/org/orgUi";
import { Panel, Stat, formatDate, useOrgStyles } from "../../components/org/orgUi";
import { useOrgPortal } from "./orgPortalContext";
import { monthEnd } from "./OrgLicenses";

export default function OrgOverview() {
    //old const { org, isOrgAdmin, goTo } = useOrgPortal();
    const { orgId, org, isOrgAdmin, goTo } = useOrgPortal();
    const s = useOrgStyles();
    //old const now = new Date();
    //old const periodLabel = formatMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`);
    // Seats = the limit Build My Biz Brand set + seats added through license requests (paid or free)
    const coachSeats = org.CoachLicenseLimit + (org.CoachLicensesPurchased || 0);
    const studentSeats = org.StudentLicenseLimit + (org.StudentLicensesPurchased || 0);

    // Plans and bought seats, for the renewal / expiring-soon box (org admins only)
    const [requests, setRequests] = useState([]);
    useEffect(() => {
        if (!isOrgAdmin) return undefined;
        let cancelled = false;
        api.get(`/org/${orgId}/license-requests`)
            .then((res) => { if (!cancelled) setRequests(res.data.requests || []); })
            .catch(() => { /* the overview works without it */ });
        return () => { cancelled = true; };
    }, [orgId, isOrgAdmin]);

    return (
        <div className="space-y-6">
            {org.OrgType === "education" && (
                <div className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${s.subPanel}`}>
                    <Info size={18} className={`mt-0.5 flex-shrink-0 ${s.muted}`} />
                    <p className={s.body}>
                        Education organizations can run programs and invite students for free. Viewing student details
                        (resume reviews, quarterly results, program progress) is available on the Enterprise plan.
                        Contact Build My Biz Brand to upgrade.
                    </p>
                </div>
            )}
            {isOrgAdmin && org.ExpiringPurchasedSeats > 0 && (
                <div role="status" className={`flex flex-col gap-3 rounded-2xl border p-4 text-sm sm:flex-row sm:items-center sm:justify-between ${s.dark ? "border-amber-800 bg-amber-900/20" : "border-amber-200 bg-amber-50"}`}>
                    <p className={s.text}>
                        <strong>{org.ExpiringPurchasedSeats} license{org.ExpiringPurchasedSeats === 1 ? "" : "s"}</strong> you bought end this month. Renew to keep them next month.
                    </p>
                    <button type="button" className={s.btnPrimary} onClick={() => goTo("licenses")}>Review and renew</button>
                </div>
            )}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <Stat label="Students" value={org.StudentCount} hint="On your roster" />
                <Stat label="Programs" value={`${org.ProgramCount} / ${org.ProgramLimit}`}
                      tone={org.ProgramCount >= org.ProgramLimit ? "warn" : "default"} hint="Active / allowed" />
                <Stat label="Coach licenses" value={`${org.CoachLicensesUsed} / ${coachSeats}`}
                      hint="Assignments this month" tone={org.CoachLicensesUsed >= coachSeats ? "warn" : "default"} />
                <Stat label="Student licenses" value={`${org.StudentLicensesUsed} / ${studentSeats}`}
                      hint="Assignments this month" tone={studentSeats > 0 && org.StudentLicensesUsed >= studentSeats ? "warn" : "default"} />
            </div>
            {isOrgAdmin && <Renewals requests={requests} onOpen={() => goTo("licenses")} />}
            <Panel title="Get started">
                <ol className={`space-y-3 text-sm ${s.body}`}>
                    <li><strong className={s.text}>1. Create a program.</strong> Each program gets an invitation code students enter under My Coaching → Programs.{" "}
                        <button type="button" className={s.btnLink} onClick={() => goTo("programs")}>Programs</button></li>
                    {/* old <li><strong className={s.text}>2. Add students.</strong> Share the code, or import a list of emails.{" "} */}
                    <li><strong className={s.text}>2. Add students.</strong> Upload your roster, then hand out each student's license code.{" "}
                        <button type="button" className={s.btnLink} onClick={() => goTo("students")}>Students</button></li>
                    {isOrgAdmin && (
                        <li><strong className={s.text}>3. Assign licenses.</strong> Licenses run from the 1st to the end of each month.
                            A license you remove can't go to someone else until next month.{" "}
                            <button type="button" className={s.btnLink} onClick={() => goTo("licenses")}>Licenses</button></li>
                    )}
                    <li><strong className={s.text}>{isOrgAdmin ? "4" : "3"}. Review progress.</strong> Students choose what to share with your coaches
                        (resume, quarterly results, program progress).</li>
                </ol>
                {org.SponsoredStudentTierName && (
                    <p className={`mt-4 text-xs ${s.muted}`}>
                        Licensed students get the {org.SponsoredStudentTierName} plan while their license is active.
                    </p>
                )}
            </Panel>
        </div>
    );
}

/**
 * When licenses renew or run out: each automatic-payment plan ("renews on" its
 * next payment date, or "ends on" the last day of that month once renewal is
 * off). Hidden when there's no plan. Bought seats that end this month are the
 * banner above (with its Review and renew button), not repeated here.
 */
function Renewals({ requests, onOpen }) {
    const s = useOrgStyles();
    const plans = requests.filter((r) => r.Status === "active" && r.AutoRenews);
    if (plans.length === 0) return null;
    const warnIcon = <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-amber-500" />;
    return (
        <Panel title="Renewals" actions={<button type="button" className={s.btnLink} onClick={onOpen}>Licenses</button>}>
            <ul className={`space-y-3 text-sm ${s.body}`}>
                {plans.map((r) => (
                    <li key={r.RequestID} className="flex items-start gap-2">
                        {r.CancelAtPeriodEnd ? warnIcon : <RefreshCw size={16} className={`mt-0.5 flex-shrink-0 ${s.muted}`} />}
                        {r.CancelAtPeriodEnd ? (
                            <span>
                                <strong className={s.text}>{r.Quantity} {r.LicenseType} licenses</strong> end on{" "}
                                <strong className={s.text}>{r.CurrentPeriodEnd ? formatDate(monthEnd(r.CurrentPeriodEnd)) : "the end of the paid period"}</strong>, renewal is off.
                            </span>
                        ) : (
                            <span>
                                <strong className={s.text}>{r.ScheduledQuantity ?? r.Quantity} {r.LicenseType} licenses</strong>{" "}
                                {r.CurrentPeriodEnd ? <>renew on <strong className={s.text}>{formatDate(r.CurrentPeriodEnd)}</strong>.</> : "renew automatically."}
                            </span>
                        )}
                    </li>
                ))}
            </ul>
        </Panel>
    );
}
